import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_217: DocMigration = {
  maxSchemaVersion: "0.0.217",
  description: "Remove wallThicknesses from roomLayout",
  apply(json: any) {
    if (json.roomLayouts) {
      const settingsSetId = uid();
      const settingsSet = {
        entityType: "SETTINGS_SET",
        name: "Wall Thickness Settings Set",
        id: settingsSetId,
        sortIndex: 0,
      };

      const wallCatalogSettingsId = uid();
      const wallCatalogSettings = {
        id: wallCatalogSettingsId,
        type: "WALL_CATALOG_SETTINGS",
        entityType: "SETTINGS",
        name: "Wall Catalog Settings",
        sortIndex: 0,
        parentId: settingsSetId,
        wallMaterials: {},
      };

      const thicknessIds: { [thickness: number]: string } = {};
      const addThicknessMaterial = (wallThickness: number) => {
        if (!thicknessIds[wallThickness]) {
          const materialId = uid();
          const material = {
            type: "STRUCTURE",
            name: `${formatInchesFraction(wallThickness)} Structural Wall Material`,
            thickness: wallThickness,
          };

          wallCatalogSettings.wallMaterials[materialId] = material;
          thicknessIds[wallThickness] = materialId;
        }
        return thicknessIds[wallThickness];
      };

      for (const roomId in json.roomLayouts) {
        const roomLayout = json.roomLayouts[roomId];
        if (!roomLayout.wallThicknesses) {
          continue;
        }

        const { wallThicknesses } = roomLayout;

        if (!roomLayout.wallAssemblies) {
          roomLayout.wallAssemblies = [];
        }

        for (const wallThicknessOverride of wallThicknesses) {
          const { thickness, edgeId } = wallThicknessOverride;

          if (typeof thickness === "number") {
            const wallThickness = thickness;
            if (wallThickness === 0) {
              // some homes have badly migrated values that need to be stripped
              continue;
            }

            const wallMaterialId = addThicknessMaterial(wallThickness);

            roomLayout.wallAssemblies.push({
              edgeId,
              wallAssemblyOverride: {
                structuralLayerOverrides: [
                  {
                    wallMaterialId,
                  },
                ],
              },
            });
          } else if (typeof thickness === "object") {
            if (thickness.type === "FORMULA") {
              const { formula } = thickness;

              let wallThickness = parseFloat(formula);

              const formulaString: string = formula;
              // because this migration is operating on only very old homes, we know
              // exactly which homes have formulas like this and can determine
              // ahead of time which operators to use and how, rather than needing to
              // use a whole parseFormula implementation.
              if (formulaString.includes("+")) {
                const items = formulaString
                  .split("+")
                  .map((item) => item.replace(/[^\d.-]/g, ""))
                  .map((item) => parseFloat(item))
                  .filter((item) => !isNaN(item));
                const sum = items.reduce((o, i) => o + i, 0);
                wallThickness = sum;
              }

              if (isNaN(wallThickness)) {
                // some formulas can use legacy parameters that can't be cast to numbers
                // these homes will need to be flagged an migrated individually
                continue;
              }
              if (wallThickness === 0) {
                // some homes have badly migrated values that need to be stripped
                continue;
              }

              const wallMaterialId = addThicknessMaterial(wallThickness);

              roomLayout.wallAssemblies.push({
                edgeId,
                wallAssemblyOverride: {
                  structuralLayerOverrides: [
                    {
                      wallMaterialId,
                    },
                  ],
                },
              });
            }
          }
        }

        delete roomLayout.wallThicknesses;
      }

      if (Object.keys(thicknessIds).length) {
        json.settingsSets[settingsSetId] = settingsSet;
        json.settings[wallCatalogSettingsId] = wallCatalogSettings;
      }
    }

    return json;
  },
};

function formatInchesFraction(inches: number, maxDenominator = 8, allowZeroFeet: boolean = false): string {
  const sign = inches < 0 ? "-" : "";
  inches = Math.abs(inches);
  const feetComponent = Math.floor(inches / 12);
  const inchComponent = Math.floor(inches) % 12;
  const decimalInch = inches - Math.floor(inches);

  // early exit for round inches
  if (decimalInch === 0) {
    if (feetComponent === 0 && !allowZeroFeet) {
      return `${sign}${inchComponent}"`;
    }

    return `${sign}${feetComponent}'-${inchComponent}"`;
  }

  const decimalInchRounded = Math.round(decimalInch * maxDenominator);
  const fractionalResult = toFraction(decimalInchRounded, maxDenominator);
  const additionalInches = fractionalResult.isFraction ? 0 : fractionalResult.resultNum ?? 0;
  const additionalFeet = inchComponent + additionalInches === 12 ? 1 : 0;

  // if fraction rounding bubbles up to feet.
  if (additionalFeet === 1) {
    return `${sign}${feetComponent + additionalFeet}'-0"`;
  }

  // if fraction rounding bubbles up to inches.
  if (decimalInchRounded === 0 || !fractionalResult.isFraction) {
    if (feetComponent === 0 && !allowZeroFeet) {
      return `${sign}${inchComponent + additionalInches}"`;
    }
    return `${sign}${feetComponent}'-${inchComponent + additionalInches}"`;
  }

  const fractionalInches = fractionalResult.isFraction ? fractionalResult.resultString : "0";

  const inchesAndFractions = inchComponent === 0 ? `${fractionalInches}"` : `${inchComponent} ${fractionalInches}"`;
  if (feetComponent === 0 && !allowZeroFeet) {
    return `${sign}${inchesAndFractions}`;
  }
  return `${sign}${feetComponent}'-${inchesAndFractions}`;
}

function toFraction(numerator: number, denominator: number) {
  const greatestCommonD = (a: number, b: number): number => {
    return b ? greatestCommonD(b, a % b) : a;
  };
  const greatestCommonDenom = greatestCommonD(numerator, denominator);

  const numVal = numerator / greatestCommonDenom;
  const denVal = denominator / greatestCommonDenom;

  if (numVal === 0) {
    return { isFraction: false, resultNum: 0 };
  }
  if (numVal === denVal) {
    return { isFraction: false, resultNum: 1 };
  }

  return { isFraction: true, resultString: `${numVal}/${denVal}` };
}

export default migration_0_0_217;
